import React, {FC, useCallback, useState, useRef, useEffect} from "react";
import { Search, ChevronDown, ChevronRight, X } from "lucide-react";
import {TreeData} from "../pages/RecordsPage/components/ActivityTypeFilter/helper";
import { TreeNode } from "../ui/tree-select";
import { Checkbox } from "../ui/checkbox";
import { cn } from "../../lib/utils";
import './style.css';

interface ICustomFilterSelect {
  allowClear?: boolean,
  showSearch?: boolean,
  loading?: boolean
  className?: string,
  onChange: (value: string[]) => void,
  filterTreeNode?: (inputValue: string, treeNode: TreeNode) => boolean,
  value?: string[],
  treeData: TreeData[],
  placeholder: string,
  onSearch?: (...args: any[]) => void,
  label: string
}

interface TreeItemProps {
  node: TreeNode
  level: number
  selectedValues: Set<string>
  expandedKeys: Set<string>
  onSelect: (value: string) => void
  onToggleExpand: (key: string) => void
  searchValue: string
  filterTreeNode?: (inputValue: string, treeNode: TreeNode) => boolean
  treeHasHierarchy: boolean
}

const TreeItem: React.FC<TreeItemProps> = ({
  node,
  level,
  selectedValues,
  expandedKeys,
  onSelect,
  onToggleExpand,
  searchValue,
  filterTreeNode,
  treeHasHierarchy,
}) => {
  const hasChildren = node.children && node.children.length > 0
  const isExpanded = expandedKeys.has(node.key)
  const isSelected = selectedValues.has(node.value)

  const matchesFilter = filterTreeNode
    ? filterTreeNode(searchValue, node)
    : !searchValue || node.name.toLowerCase().includes(searchValue.toLowerCase())

  const hasMatchingChildren = node.children?.some(child => {
    if (filterTreeNode) {
      return filterTreeNode(searchValue, child) ||
        child.children?.some(grandChild => filterTreeNode(searchValue, grandChild))
    }
    return child.name.toLowerCase().includes(searchValue.toLowerCase()) ||
      child.children?.some(grandChild => grandChild.name.toLowerCase().includes(searchValue.toLowerCase()))
  })

  if (searchValue && !matchesFilter && !hasMatchingChildren) {
    return null
  }

  const shouldExpand = searchValue ? (hasMatchingChildren || matchesFilter) : isExpanded

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-1 py-1 px-2 rounded-sm cursor-pointer hover:bg-muted",
          node.disabled && "opacity-50 cursor-not-allowed"
        )}
        style={{ paddingLeft: `${level * 16 + 4}px` }}
        onClick={() => {
          if (node.disabled) return
          onSelect(node.value)
        }}
      >
        {hasChildren ? (
          <button
            type="button"
            className="p-0.5 hover:bg-muted rounded"
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(node.key)
            }}
          >
            {shouldExpand ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
        ) : treeHasHierarchy ? (
          <span className="w-5" />
        ) : null}

        <Checkbox
          checked={isSelected}
          disabled={node.disabled}
          onCheckedChange={() => !node.disabled && onSelect(node.value)}
          onClick={(e) => e.stopPropagation()}
        />

        <span className="flex-1 text-sm truncate">{node.title}</span>
      </div>

      {hasChildren && shouldExpand && (
        <div>
          {node.children!.map((child) => (
            <TreeItem
              key={child.key}
              node={child as TreeNode}
              level={level + 1}
              selectedValues={selectedValues}
              expandedKeys={expandedKeys}
              onSelect={onSelect}
              onToggleExpand={onToggleExpand}
              searchValue={searchValue}
              filterTreeNode={filterTreeNode}
              treeHasHierarchy={treeHasHierarchy}
            />
          ))}
        </div>
      )}
    </div>
  )
}

const CustomTreeSelect: FC<ICustomFilterSelect> = ({
  showSearch,
  className,
  onChange,
  loading,
  filterTreeNode,
  value,
  treeData,
  placeholder,
  label,
}) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const valuesNumber = value?.length || 0;

  const selectedValues = React.useMemo(() => {
    return new Set(value || []);
  }, [value]);

  const treeHasHierarchy = React.useMemo(() => {
    const checkForChildren = (nodes: TreeData[]): boolean => {
      return nodes.some(node => node.children && node.children.length > 0);
    };
    return checkForChildren(treeData);
  }, [treeData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = useCallback((nodeValue: string) => {
    const newValues = new Set(selectedValues);
    if (newValues.has(nodeValue)) {
      newValues.delete(nodeValue);
    } else {
      newValues.add(nodeValue);
    }
    onChange(Array.from(newValues));
  }, [selectedValues, onChange]);

  const handleToggleExpand = useCallback((key: string) => {
    setExpandedKeys(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(key)) {
        newExpanded.delete(key);
      } else {
        newExpanded.add(key);
      }
      return newExpanded;
    });
  }, []);

  const handleInputFocus = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
    setSearchValue("");
  }, [onChange]);

  return (
    <div className="CustomTreeSelect" ref={containerRef}>
      <div
        className={cn(
          "flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white dark:bg-muted shadow-sm border-border/30 dark:border-transparent cursor-text",
          open && "ring-1 ring-ring"
        )}
        onClick={() => inputRef.current?.focus()}
      >
        <Search className="h-4 w-4 text-muted-foreground shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={label}
          className="bg-transparent outline-none text-sm w-[50px] focus:w-[100px] transition-all placeholder:text-foreground"
        />
        {valuesNumber > 0 && (
          <span className="bg-muted text-muted-foreground px-1.5 py-0.5 rounded text-xs font-medium">
            {valuesNumber}
          </span>
        )}
        {valuesNumber > 0 && (
          <button
            type="button"
            onClick={handleClear}
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {open && (
        <div className="absolute z-50 mt-1 w-max min-w-[180px] max-w-[280px] rounded-md border bg-popover shadow-md">
          <div className="max-h-[300px] overflow-y-auto p-1">
            {(treeData as TreeNode[]).map((node) => (
              <TreeItem
                key={node.key}
                node={node}
                level={0}
                selectedValues={selectedValues}
                expandedKeys={expandedKeys}
                onSelect={handleSelect}
                onToggleExpand={handleToggleExpand}
                searchValue={searchValue}
                filterTreeNode={filterTreeNode}
                treeHasHierarchy={treeHasHierarchy}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomTreeSelect;
